/**
 * LastUpdated Component
 * 
 * Displays a "Last updated" timestamp with a clock icon.
 * Used in page headers and subbars.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { colors, typography as typographyTokens } from '../../tokens';

export interface LastUpdatedProps {
    /** The date/time to display */
    date: Date | string | null;
    /** Optional prefix text (default: "Last updated:") */
    prefix?: string;
    /** Whether to show the icon (default: true) */
    showIcon?: boolean;
}

const formatTimestamp = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    
    // Check if date is valid
    if (isNaN(d.getTime())) {
        return 'N/A';
    }
    
    const hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12;
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear();
    
    return `${hour12}:${minutes}${ampm} – ${month}/${day}/${year}`;
};

export const LastUpdated: React.FC<LastUpdatedProps> = ({
    date,
    prefix = 'Last updated:',
    showIcon = true,
}) => {
    if (!date) {
        return null;
    }

    const formattedDate = formatTimestamp(date);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
            }}>
            {showIcon && (
                <AccessTimeIcon
                    sx={{
                        fontSize: 16,
                        color: colors.neutral[500],
                    }}
                />
            )}
            <Typography
                sx={{
                    fontSize: typographyTokens.fontSize.sm,
                    color: colors.neutral[500],
                    fontWeight: typographyTokens.fontWeight.normal,
                }}>
                {prefix} {formattedDate}
            </Typography>
        </Box>
    );
};

export default LastUpdated;

